import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
  Currency,
  PermissionEnum,
  Response,
  apiAbortController,
} from '@ui/src';
import api from '../../api/api';

import { Factor } from '../../hoc/types';
import { TransactionType } from './types';

export interface PermissionResponse {
  permissions: PermissionEnum[];
}

export const amlDictionariesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getAmlTransactionTypes: build.query<Response<TransactionType>, void>({
      query: () => ({
        url: '/v1/transactions-types',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAmlPermissions: build.query<{ data: PermissionResponse }, void>({
      query: () => ({
        url: '/v1/employees/whoami',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAmlDictionaries: build.query<any, void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const dictionaries = await Promise.all([
          fetchWithBQ('/v1/risk-score-ranges?all=true'),
        ]);

        return dictionaries
          ? { data: { riskScoreRanges: dictionaries[0].data } }
          : { error: { status: 'FETCH_ERROR' } as FetchBaseQueryError };
      },
    }),
    getAmlCurrencies: build.query<Response<Currency>, void>({
      query: () => ({
        url: '/v1/currencies?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAmlBaseCurrencies: build.query<{ data: Currency }, void>({
      query: () => ({
        url: '/v1/currencies/base',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getFactors: build.query<Response<Factor>, void>({
      query: () => ({
        url: '/v1/factors?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateFactor: build.mutation<
      { data: Factor },
      { body: Factor; id: string }
    >({
      query: (data: { body: Factor; id: string }) => ({
        url: `/v1/factors/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAmlWhitelabel: build.query<
      { data: { data: { value: Record<string, string> } } },
      void
    >({ query: () => ({ url: '/v1/settings/whitelabel' }) }),
  }),
});

export const {
  useGetAmlTransactionTypesQuery,
  useLazyGetAmlPermissionsQuery,
  useUpdateFactorMutation,
  useLazyGetAmlDictionariesQuery,
  useLazyGetAmlBaseCurrenciesQuery,
  useLazyGetAmlCurrenciesQuery,
  useLazyGetFactorsQuery,
  useLazyGetAmlWhitelabelQuery,
} = amlDictionariesApi;

export const {
  endpoints: {
    getAmlTransactionTypes,
    getAmlPermissions,
    updateFactor,
    getAmlDictionaries,
    getFactors,
    getAmlBaseCurrencies,
    getAmlCurrencies,
  },
} = amlDictionariesApi;
